// ─── Report filter selections ────────────────────────────────────────────────
//
// What the reader has ticked in the report's filter menus, kept in the URL
// alongside the period so a shared link reproduces the whole view and not just
// half of it.
//
// Each dimension travels as `f_<id>=a,b`. The `f_` prefix keeps the namespace
// clear of the period's own params (`range`, `from`, `to`, `grain`) and of
// anything a later feature adds, so a dimension can be called `from` without
// colliding with a date.
//
// Values are validated against the catalog on the way in: a hand-edited or
// stale URL naming an option that no longer exists drops that value rather than
// filtering the report down to nothing. Dimensions whose options are rows
// rather than an enum (`options: null`) cannot be validated here, so their
// values pass through and the report itself is the one to reject them.
//
// Pure and dependency-free so it can be unit-tested.

import type { ReportDefinition, ReportFilterDimension } from '@/lib/reportCatalog'

/** Dimension id → the selected option ids. An absent key means "all". */
export type ReportFilterSelection = Record<string, string[]>

const PREFIX = 'f_'

/** Read a report's filter selection out of the URL. */
export function parseFilters(
  params: URLSearchParams,
  report: ReportDefinition | null,
): ReportFilterSelection {
  if (!report) return {}
  const selection: ReportFilterSelection = {}
  for (const dimension of report.filters) {
    const raw = params.get(`${PREFIX}${dimension.id}`)
    if (!raw) continue
    const values = keepValid(raw.split(',').map((v) => v.trim()), dimension)
    if (values.length > 0) selection[dimension.id] = values
  }
  return selection
}

/**
 * Apply a selection to a `URLSearchParams`, clearing every dimension key first.
 *
 * Clearing is what makes moving between reports safe: two reports can share a
 * dimension id, and a stale `f_kind` left behind by Storage would otherwise
 * silently narrow an Equipment report.
 */
export function applyFilters(
  params: URLSearchParams,
  selection: ReportFilterSelection,
): URLSearchParams {
  const next = new URLSearchParams(params)
  for (const key of [...next.keys()]) {
    if (key.startsWith(PREFIX)) next.delete(key)
  }
  for (const [id, values] of Object.entries(selection)) {
    if (values.length > 0) next.set(`${PREFIX}${id}`, values.join(','))
  }
  return next
}

/** Tick or untick one option. Removes the key entirely once nothing is left. */
export function toggleFilter(
  selection: ReportFilterSelection,
  dimensionId: string,
  optionId: string,
): ReportFilterSelection {
  const current = selection[dimensionId] ?? []
  const next = current.includes(optionId)
    ? current.filter((v) => v !== optionId)
    : [...current, optionId]

  const out = { ...selection }
  if (next.length > 0) out[dimensionId] = next
  else delete out[dimensionId]
  return out
}

/** Drop one dimension's selection. */
export function clearDimension(
  selection: ReportFilterSelection,
  dimensionId: string,
): ReportFilterSelection {
  const out = { ...selection }
  delete out[dimensionId]
  return out
}

function keepValid(values: string[], dimension: ReportFilterDimension): string[] {
  const seen = new Set<string>()
  return values.filter((value) => {
    if (value === '' || seen.has(value)) return false
    // Row-backed dimensions have nothing to validate against here.
    if (dimension.options && !dimension.options.some((o) => o.id === value)) return false
    seen.add(value)
    return true
  })
}
